import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';
import { useLanguage } from '../context/LanguageContext';

const StaffDashboard = () => {
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  const [complaints, setComplaints] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterArea, setFilterArea] = useState('');

  // Update & Assign Modal state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newAgentId, setNewAgentId] = useState('');
  const [newEmployeeId, setNewEmployeeId] = useState('');
  const [newDeptId, setNewDeptId] = useState('');
  const [newAreaId, setNewAreaId] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolutionImage, setResolutionImage] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchStaffData = useCallback(async () => {
    try {
      let url = '/complaints?';
      if (filterStatus) url += `status=${encodeURIComponent(filterStatus)}&`;
      if (filterPriority) url += `priority=${encodeURIComponent(filterPriority)}&`;
      if (filterDept) url += `department_id=${encodeURIComponent(filterDept)}&`;
      if (filterArea) url += `area_id=${encodeURIComponent(filterArea)}&`;

      const [cRes, dRes, aRes, uRes] = await Promise.all([
        api.get(url),
        api.get('/departments'),
        api.get('/areas'),
        api.get('/auth/users')
      ]);

      setComplaints(cRes.data);
      setDepartments(dRes.data);
      setAreas(aRes.data);
      setStaffUsers(uRes.data);
    } catch (err) {
      console.error('Error loading staff dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, filterDept, filterArea]);

  useEffect(() => {
    fetchStaffData();
  }, [fetchStaffData]);

  const handleOpenUpdateModal = (ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status || 'Submitted');
    setNewAgentId(ticket.assigned_agent_id || '');
    setNewEmployeeId(ticket.assigned_employee_id || '');
    setNewDeptId(ticket.department_id || '');
    setNewAreaId(ticket.area_id || '');
    setNewPriority(ticket.priority || 'Medium');
    setResolutionNotes(ticket.resolution_notes || '');
    setResolutionImage(null);
  };

  const handleSaveUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTicket) return;

    setUpdating(true);
    setMsg('');

    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      formData.append('assigned_employee_id', newEmployeeId || '');
      formData.append('assigned_agent_id', newAgentId || '');
      if (newDeptId) formData.append('department_id', newDeptId);
      if (newAreaId) formData.append('area_id', newAreaId);
      if (newPriority) formData.append('priority', newPriority);
      if (resolutionNotes) formData.append('resolution_notes', resolutionNotes);
      if (resolutionImage) formData.append('resolution_image', resolutionImage);

      await api.put(`/complaints/${selectedTicket.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setMsg(`Ticket ${selectedTicket.ticket_number} updated successfully.`);
      setSelectedTicket(null);
      fetchStaffData();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Error updating ticket.');
    } finally {
      setUpdating(false);
    }
  };

  const handleEscalate = async (id) => {
    if (!window.confirm('Are you sure you want to escalate this complaint?')) return;
    try {
      await api.post(`/complaints/${id}/escalate`, { reason: 'Escalated from management review' });
      fetchStaffData();
    } catch (err) {
      alert(err.response?.data?.message || 'Escalation failed.');
    }
  };

  // Filter department employees for assignment based on ticket department
  const currentDeptId = selectedTicket?.department_id || user?.department_id;
  const departmentEmployees = staffUsers.filter(u => 
    u.role === 'employee' && (currentDeptId ? Number(u.department_id) === Number(currentDeptId) : true)
  );

  // Compute KPI metrics
  const totalCount = complaints.length;
  const submittedCount = complaints.filter(c => c.status === 'Submitted').length;
  const activeCount = complaints.filter(c => c.status === 'Assigned' || c.status === 'In Progress').length;
  const resolvedCount = complaints.filter(c => c.status === 'Resolved' || c.status === 'Closed').length;
  const escalatedCount = complaints.filter(c => c.status === 'Escalated').length;

  // Filter complaints with search query
  const filteredComplaints = complaints.filter(c => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      c.ticket_number?.toLowerCase().includes(term) ||
      c.title?.toLowerCase().includes(term) ||
      c.customer_name?.toLowerCase().includes(term) ||
      c.department_name?.toLowerCase().includes(term) ||
      c.area_name?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="container py-4">
      {/* Header Banner */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="brand-font fw-bold text-dark mb-1">
            {user?.role === 'admin' ? 'Administrator Central Command Desk' :
             user?.role === 'dept_head' ? t('deptHeadDesk') :
             user?.role === 'employee' ? t('employeeDesk') :
             user?.role === 'representative' ? t('repDesk') : t('staffCommandCenter')}
          </h2>
          <p className="text-muted mb-0">
            {t('role')}: <span className="badge bg-primary text-capitalize me-2">{user?.role?.replace('_', ' ')}</span>
            {user?.role === 'representative' && user?.area_name && (
              <span className="badge bg-success font-monospace me-2">{t('ward')}: {user.area_name}</span>
            )}
            {user?.department_name && <span>{t('departments')}: <strong>{user.department_name}</strong></span>}
          </p>
        </div>

        {user?.role === 'admin' && (
          <div className="d-flex gap-2">
            <Link to="/admin/users" className="btn btn-outline-primary btn-sm">
              <i className="bi bi-people me-1"></i> {t('staffUsers')}
            </Link>
            <Link to="/admin/departments" className="btn btn-outline-primary btn-sm">
              <i className="bi bi-building me-1"></i> {t('departments')}
            </Link>
            <Link to="/admin/areas" className="btn btn-outline-primary btn-sm">
              <i className="bi bi-geo-alt me-1"></i> {t('areaMaster')}
            </Link>
          </div>
        )}
      </div>

      {/* Role Context Alerts */}
      {user?.role === 'admin' && (
        <div className="alert alert-danger border-danger bg-danger bg-opacity-10 d-flex align-items-center mb-4" role="alert">
          <i className="bi bi-shield-lock-fill fs-3 me-3 text-danger"></i>
          <div>
            <h6 className="fw-bold mb-0 text-danger">System Administrator Central Oversight Active</h6>
            <small className="text-dark">Full visibility over all incoming complaints across all municipal departments, wards, and staff assignments.</small>
          </div>
        </div>
      )}

      {user?.role === 'dept_head' && (
        <div className="alert alert-primary border-primary d-flex align-items-center mb-4" role="alert">
          <i className="bi bi-building-gear fs-3 me-3 text-primary"></i>
          <div>
            <h6 className="fw-bold mb-0">Department Head Command Active</h6>
            <small>You are managing <strong>{user.department_name || 'your department'}</strong> complaints. Click <strong>"Assign Employee"</strong> to assign field staff to resolve complaints.</small>
          </div>
        </div>
      )}

      {user?.role === 'employee' && (
        <div className="alert alert-info border-info d-flex align-items-center mb-4" role="alert">
          <i className="bi bi-person-workspace fs-3 me-3 text-info"></i>
          <div>
            <h6 className="fw-bold mb-0">Employee Work Queue</h6>
            <small>Displaying complaints assigned directly to you by your Department Head.</small>
          </div>
        </div>
      )}

      {user?.role === 'representative' && (
        <div className="alert alert-success border-success bg-success bg-opacity-10 d-flex align-items-center mb-4" role="alert">
          <i className="bi bi-geo-alt-fill fs-3 me-3 text-success"></i>
          <div>
            <h6 className="fw-bold mb-0 text-success">Ward Oversight Active</h6>
            <small className="text-dark">Showing all citizen complaints registered within <strong>{user.area_name || 'your assigned Ward'}</strong>.</small>
          </div>
        </div>
      )}

      {msg && <div className="alert alert-success py-2 small mb-4 alert-dismissible fade show">{msg}</div>}

      {/* KPI Stats Cards (Admin, Dept Head, Representative) */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card glass-card border-0 p-3 shadow-sm h-100">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-primary bg-opacity-10 text-primary rounded-3 p-3">
                <i className="bi bi-collection fs-3"></i>
              </div>
              <div>
                <div className="text-muted small fw-medium">{t('totalTickets')}</div>
                <div className="fs-3 fw-bold text-dark">{totalCount}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="card glass-card border-0 p-3 shadow-sm h-100">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-info bg-opacity-10 text-info rounded-3 p-3">
                <i className="bi bi-hourglass-split fs-3"></i>
              </div>
              <div>
                <div className="text-muted small fw-medium">{t('inProgress')} / {t('status_Assigned')}</div>
                <div className="fs-3 fw-bold text-info">{activeCount + submittedCount}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="card glass-card border-0 p-3 shadow-sm h-100">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-success bg-opacity-10 text-success rounded-3 p-3">
                <i className="bi bi-check2-circle fs-3"></i>
              </div>
              <div>
                <div className="text-muted small fw-medium">{t('resolved')}</div>
                <div className="fs-3 fw-bold text-success">{resolvedCount}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="card glass-card border-0 p-3 shadow-sm h-100">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-danger bg-opacity-10 text-danger rounded-3 p-3">
                <i className="bi bi-exclamation-octagon fs-3"></i>
              </div>
              <div>
                <div className="text-muted small fw-medium">{t('escalated')} (SLA Breached)</div>
                <div className="fs-3 fw-bold text-danger">{escalatedCount}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters Bar */}
      <div className="card glass-card border-0 p-3 mb-4">
        <div className="row g-3 align-items-center">
          <div className="col-md-4">
            <label className="form-label small fw-semibold text-secondary mb-1">Search Tickets</label>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
              <input
                type="text"
                className="form-control"
                placeholder="Ticket #, Citizen, Subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-2">
            <label className="form-label small fw-semibold text-secondary mb-1">{t('tableStatus')}</label>
            <select
              className="form-select form-select-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">{t('allStatuses')}</option>
              <option value="Submitted">{t('status_Submitted')}</option>
              <option value="Assigned">{t('status_Assigned')}</option>
              <option value="In Progress">{t('status_InProgress')}</option>
              <option value="Pending Customer">{t('status_PendingCustomer')}</option>
              <option value="Resolved">{t('status_Resolved')}</option>
              <option value="Closed">{t('status_Closed')}</option>
              <option value="Escalated">{t('status_Escalated')}</option>
            </select>
          </div>

          <div className="col-md-2">
            <label className="form-label small fw-semibold text-secondary mb-1">{t('priorityLevel')}</label>
            <select
              className="form-select form-select-sm"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="">All Priorities</option>
              <option value="Low">{t('priority_Low')}</option>
              <option value="Medium">{t('priority_Medium')}</option>
              <option value="High">{t('priority_High')}</option>
            </select>
          </div>

          {user?.role !== 'dept_head' && user?.role !== 'employee' && (
            <div className="col-md-2">
              <label className="form-label small fw-semibold text-secondary mb-1">{t('allDepartments')}</label>
              <select
                className="form-select form-select-sm"
                value={filterDept}
                onChange={(e) => setFilterDept(e.target.value)}
              >
                <option value="">{t('allDepartments')}</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}

          {user?.role !== 'representative' && (
            <div className="col-md-2">
              <label className="form-label small fw-semibold text-secondary mb-1">{t('allAreas')}</label>
              <select
                className="form-select form-select-sm"
                value={filterArea}
                onChange={(e) => setFilterArea(e.target.value)}
              >
                <option value="">{t('allAreas')}</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Complaints List Table */}
      <div className="card glass-card border-0 p-4">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted small mt-2">{t('loadingTickets')}</p>
          </div>
        ) : filteredComplaints.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-check-all fs-1 d-block mb-2 text-success"></i>
            <p className="fw-semibold mb-0">{t('noComplaintsFound')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t('tableTicketNum')}</th>
                  <th>Citizen & Title</th>
                  <th>{t('tableAreaLoc')}</th>
                  <th>{t('tableDept')}</th>
                  <th>{t('tablePriority')}</th>
                  <th>{t('tableStatus')}</th>
                  <th>{t('tableAssignedEmp')}</th>
                  <th className="text-end">{t('tableAction')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredComplaints.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span className="font-monospace fw-bold text-primary">{c.ticket_number}</span>
                      <small className="d-block text-muted">
                        {new Date(c.created_at).toLocaleDateString()}
                      </small>
                    </td>
                    <td>
                      <div className="fw-bold text-dark">{c.title}</div>
                      <div className="small text-muted">
                        <i className="bi bi-person me-1"></i>{c.customer_name}
                      </div>
                    </td>
                    <td>
                      {c.area_name ? (
                        <div>
                          <span className="fw-semibold">{c.area_name}</span>
                          {c.sub_area_name && (
                            <span className="badge bg-light text-secondary border d-block mt-1">
                              {c.sub_area_name}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted small italic">{t('notSpecified')}</span>
                      )}
                    </td>
                    <td>
                      {c.department_name ? (
                        <span className="badge bg-light text-primary border">{c.department_name}</span>
                      ) : (
                        <span className="text-muted italic">{t('unassigned')}</span>
                      )}
                    </td>
                    <td><StatusBadge priority={c.priority} /></td>
                    <td><StatusBadge status={c.status} /></td>
                    <td>
                      {c.assigned_employee_name ? (
                        <span className="badge bg-info text-white">
                          <i className="bi bi-person-check-fill me-1"></i>{c.assigned_employee_name}
                        </span>
                      ) : (
                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning">
                          <i className="bi bi-person-x me-1"></i>{t('unassigned')}
                        </span>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <Link to={`/complaints/${c.id}`} className="btn btn-outline-primary" title={t('viewDetails')}>
                          <i className="bi bi-eye me-1"></i> Details
                        </Link>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => handleOpenUpdateModal(c)}
                          title="Assign Employee & Update Status"
                        >
                          <i className="bi bi-pencil-square me-1"></i>
                          {user?.role === 'dept_head' ? 'Assign / Update' : t('edit')}
                        </button>
                        {c.status !== 'Escalated' && (
                          <button
                            className="btn btn-outline-danger"
                            title={t('requestEscalation')}
                            onClick={() => handleEscalate(c.id)}
                          >
                            <i className="bi bi-exclamation-triangle"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Quick Edit / Assign Employee Ticket */}
      {selectedTicket && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-bottom bg-light">
                <h5 className="modal-title brand-font fw-bold">
                  <i className="bi bi-pencil-square text-primary me-2"></i>
                  {t('manageTicket')}: {selectedTicket?.ticket_number}
                </h5>
                <button type="button" className="btn-close" onClick={() => setSelectedTicket(null)}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleSaveUpdate}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('updateStatus')}</label>
                    <select
                      className="form-select"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      <option value="Submitted">{t('status_Submitted')}</option>
                      <option value="Assigned">{t('status_Assigned')}</option>
                      <option value="In Progress">{t('status_InProgress')}</option>
                      <option value="Pending Customer">{t('status_PendingCustomer')}</option>
                      <option value="Resolved">{t('status_Resolved')}</option>
                      <option value="Closed">{t('status_Closed')}</option>
                      <option value="Escalated">{t('status_Escalated')}</option>
                    </select>
                  </div>

                  {/* Department Employee Assignment */}
                  <div className="mb-3 p-3 rounded-3 bg-primary bg-opacity-10 border border-primary border-opacity-25">
                    <label className="form-label fw-semibold small text-primary d-flex justify-content-between">
                      <span><i className="bi bi-person-check-fill me-1"></i> {t('assignDeptEmployee')}</span>
                      <span className="badge bg-primary">{departmentEmployees.length} Available</span>
                    </label>
                    <select
                      className="form-select border-primary"
                      value={newEmployeeId}
                      onChange={(e) => {
                        setNewEmployeeId(e.target.value);
                        if (e.target.value && newStatus === 'Submitted') {
                          setNewStatus('Assigned');
                        }
                      }}
                    >
                      <option value="">{t('noEmpAssigned')}</option>
                      {departmentEmployees.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.full_name} ({e.email})
                        </option>
                      ))}
                      {/* Fallback for admins to assign any employee across departments */}
                      {user?.role === 'admin' && staffUsers
                        .filter(u => u.role === 'employee' && !departmentEmployees.some(de => de.id === u.id))
                        .map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.full_name} ({e.department_name || 'Other Dept'})
                          </option>
                        ))}
                    </select>
                    <div className="form-text extra-small text-muted">
                      Selecting an employee will assign the ticket directly to their work queue.
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('priorityLevel')}</label>
                    <select
                      className="form-select"
                      value={newPriority}
                      onChange={(e) => setNewPriority(e.target.value)}
                    >
                      <option value="Low">{t('priority_Low')} (72h SLA)</option>
                      <option value="Medium">{t('priority_Medium')} (48h SLA)</option>
                      <option value="High">{t('priority_High')} (24h SLA)</option>
                    </select>
                  </div>

                  <div className="p-3 mb-4 rounded-3 bg-success bg-opacity-10 border border-success border-opacity-25">
                    <label className="form-label fw-semibold small text-success">
                      <i className="bi bi-camera-fill me-1"></i> {t('attachSolutionPhoto')}
                    </label>
                    <input
                      type="file"
                      className="form-control mb-2"
                      accept="image/*"
                      onChange={(e) => setResolutionImage(e.target.files[0] || null)}
                    />

                    <label className="form-label fw-semibold small text-success mt-2">
                      <i className="bi bi-journal-check me-1"></i> {t('resolutionRemarksSummary')}
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder={t('resolutionPlaceholder')}
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                    ></textarea>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-light rounded-pill px-4" onClick={() => setSelectedTicket(null)}>
                      {t('cancel')}
                    </button>
                    <button type="submit" className="btn btn-primary-gradient px-4" disabled={updating}>
                      {updating ? t('saving') : t('saveChanges')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffDashboard;
